import { useState, useEffect, useCallback } from 'react';
import { signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

const useUserId = () => {
    const [userId, setUserId] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!auth) {
            console.warn('Firebase Auth nie jest zainicjalizowany');
            // Fallback do lokalnego ID (niezabezpieczone)
            const fallbackId = localStorage.getItem('user_id_v1') ||
                'local_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('user_id_v1', fallbackId);
            setUserId(fallbackId);
            setLoading(false);
            return;
        }

        // Nasłuchuj zmian stanu uwierzytelnienia
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            try {
                if (user) {
                    // Sprawdź zgodę na cookies
                    const cookieConsent = localStorage.getItem('cookieConsent');
                    
                    if (cookieConsent === 'rejected') {
                        // Użytkownik odrzucił zgodę - wyloguj z Firebase
                        console.log('Zgoda odrzucona - wylogowanie z Firebase');
                        await auth.signOut();
                        
                        // Użyj lokalnego ID
                        const fallbackId = localStorage.getItem('user_id_v1') ||
                            'local_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
                        localStorage.setItem('user_id_v1', fallbackId);
                        setUserId(fallbackId);
                        setLoading(false);
                        return;
                    }
                    
                    // Użytkownik jest już zalogowany i ma zgodę
                    console.log('Użytkownik zalogowany:', user.uid);
                    setUserId(user.uid);
                } else {
                    // Sprawdź zgodę na cookies przed utworzeniem konta Firebase
                    const cookieConsent = localStorage.getItem('cookieConsent');
                    
                    if (cookieConsent !== 'accepted') {
                        console.log('Brak zgody na cookies - używam lokalnego ID');
                        // Użyj lokalnego ID bez tworzenia konta Firebase
                        const fallbackId = localStorage.getItem('user_id_v1') ||
                            'local_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
                        localStorage.setItem('user_id_v1', fallbackId);
                        setUserId(fallbackId);
                        setLoading(false);
                        return;
                    }

                    // Użytkownik wyraził zgodę - utwórz konto Firebase
                    console.log('Pobieranie sesji anonimowej z serwera Node.js...');

                    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

                    const response = await fetch(`${API_URL}/api/auth/anonymous`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    });

                    if (!response.ok) {
                        throw new Error(`Błąd serwera: ${response.statusText}`);
                    }

                    const data = await response.json();

                    console.log('Otrzymano token z backendu, logowanie...');
                    await signInWithCustomToken(auth, data.token);
                    // Po pomyślnym zalogowaniu, onAuthStateChanged odpali się ponownie i wejdzie w if(user)
                }
            } catch (error) {
                console.error('Błąd podczas logowania anonimowego:', error);

                // Fallback do lokalnego ID w przypadku błędu
                const fallbackId = localStorage.getItem('user_id_v1') ||
                    'local_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
                localStorage.setItem('user_id_v1', fallbackId);
                setUserId(fallbackId);
            } finally {
                setLoading(false);
            }
        });

        // Cleanup subscription
        return () => unsubscribe();
    }, []);

    const getIdToken = useCallback(async () => {
        if (!auth?.currentUser) {
            throw new Error('Użytkownik nie jest zalogowany');
        }
        return await auth.currentUser.getIdToken();
    }, []);

    return { userId, loading, getIdToken };
};

export default useUserId;